import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import 'regenerator-runtime/runtime';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '~/queryClient';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'theme-ui';
import { PersistGate } from 'redux-persist/integration/react';
import FlagProvider, { UnleashClient } from '@unleash/proxy-client-react';

// base
import 'normalize.css';
import '~/ui/assets/stylesheets/base/custom-defaults.scss';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-phone-number-input/style.css';

// components
import '~/ui/assets/stylesheets/components/units.scss';
import '~/ui/assets/stylesheets/components/tray.scss';
import '~/ui/assets/stylesheets/components/field.scss';
import '~/ui/assets/stylesheets/components/tabs.scss';
import '~/ui/assets/stylesheets/components/label.scss';
import '~/ui/assets/stylesheets/components/heading.scss';
import '~/ui/assets/stylesheets/components/sub-heading.scss';
// controls
import '~/ui/assets/stylesheets/controls/button.scss';
import '~/ui/assets/stylesheets/controls/search.scss';
import '~/ui/assets/stylesheets/controls/radio-list-item.scss';
import '~/ui/assets/stylesheets/controls/icon-wrapper.scss';
// skins
import '~/ui/assets/stylesheets/skins/skins.scss';
// utilities
import '~/ui/assets/stylesheets/utilities/typography.scss';
import '~/ui/assets/stylesheets/utilities/width.scss';
import '~/ui/assets/stylesheets/utilities/position.scss';
import '~/ui/assets/stylesheets/utilities/transform.scss';
import '~/ui/assets/stylesheets/utilities/justify-self.scss';
import '~/ui/assets/stylesheets/utilities/margin.scss';
import '~/ui/assets/stylesheets/utilities/padding.scss';
import '~/ui/assets/stylesheets/utilities/font-size.scss';
import '~/ui/assets/stylesheets/utilities/pointer-events.scss';
import '~/ui/assets/stylesheets/utilities/display.scss';
import '~/ui/assets/stylesheets/utilities/align-items.scss';
import '~/ui/assets/stylesheets/utilities/text-transform.scss';
import '~/ui/assets/stylesheets/utilities/text-overflow.scss';
import '~/ui/assets/stylesheets/utilities/white-space.scss';
import '~/ui/assets/stylesheets/utilities/flex-direction.scss';
import '~/ui/assets/stylesheets/utilities/justify-content.scss';
import '~/ui/assets/stylesheets/utilities/flex-wrap.scss';
import '~/ui/assets/stylesheets/utilities/column-gap.scss';
import '~/ui/assets/stylesheets/utilities/row-gap.scss';

import '~/i18n';

import { store, persistor } from '~/store';
import App from '~/components/App';
import theme from '~/ui/theme';
import { ToastContextProvider } from '~/contexts/ToastContext';
import { getAppVersion } from '~/utils/general-utils';
import {
    getFeatureFlagsConfig,
    FeatureFlag
} from '~/utils/feature-flags-utils';
import { initLaunchDarklyProvider } from '~/components/LaunchDarklyProvider';
import { GoogleMapsApiProvider } from '~/components/GoogleMapsApiProvider';

// ensure Interstellar CSS variables are declared last
import '~/ui/assets/stylesheets/base/color.scss';
import '~/ui/assets/stylesheets/base/sizes.scss';
import '~/ui/assets/stylesheets/base/dimensions.scss';
import '~/ui/assets/stylesheets/base/z-index.scss';
import '~/ui/assets/stylesheets/base/weights.scss';

const featureFlagConfig = getFeatureFlagsConfig(window.location.hostname);
const client = new UnleashClient(featureFlagConfig);

// output current react version for dev and testing environments
client.on('ready', () => {
    if (!client.isEnabled(FeatureFlag.GLOBAL)) return;

    console.info('Flagged features are visible');
    console.info(`React.version: ${React.version}`);
    console.info(`App version: ${getAppVersion()}`);
    console.info(`Google Maps API version: ${window.google?.maps?.version}`);
});

const renderApp = async () => {
    const LDProvider = await initLaunchDarklyProvider();

    ReactDOM.render(
        <LDProvider>
            <FlagProvider unleashClient={client}>
                <Provider store={store}>
                    <QueryClientProvider client={queryClient}>
                        <PersistGate loading={null} persistor={persistor}>
                            <ThemeProvider theme={theme}>
                                <ToastContextProvider>
                                    <GoogleMapsApiProvider>
                                        <Suspense fallback={null}>
                                            <App />
                                        </Suspense>
                                    </GoogleMapsApiProvider>
                                </ToastContextProvider>
                            </ThemeProvider>
                        </PersistGate>
                    </QueryClientProvider>
                </Provider>
            </FlagProvider>
        </LDProvider>,
        document.getElementById('root')
    );
};

renderApp();
